import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-new-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title>Set New Password</h2>
    <mat-dialog-content>
      <p>Your account requires a new password. Please create a new password below.</p>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>New Password</mat-label>
        <input matInput
               [type]="hidePassword ? 'password' : 'text'"
               [(ngModel)]="newPassword"
               placeholder="Enter new password">
        <button mat-icon-button matSuffix (click)="hidePassword = !hidePassword" type="button">
          <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
        </button>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Confirm Password</mat-label>
        <input matInput
               [type]="hideConfirmPassword ? 'password' : 'text'"
               [(ngModel)]="confirmPassword"
               placeholder="Confirm new password">
        <button mat-icon-button matSuffix (click)="hideConfirmPassword = !hideConfirmPassword" type="button">
          <mat-icon>{{hideConfirmPassword ? 'visibility_off' : 'visibility'}}</mat-icon>
        </button>
      </mat-form-field>

      @if (errorMessage) {
        <p class="error-message">{{ errorMessage }}</p>
      }

      <div class="password-requirements">
        <p><strong>Password requirements:</strong></p>
        <ul>
          <li>At least 8 characters</li>
          <li>Contains uppercase and lowercase letters</li>
          <li>Contains at least one number</li>
          <li>Contains at least one special character</li>
        </ul>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="isSubmitting">
        {{ isSubmitting ? 'Setting Password...' : 'Set Password' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .full-width {
      width: 100%;
      margin-bottom: 8px;
    }
    .error-message {
      color: #f44336;
      font-size: 14px;
      margin: 8px 0;
    }
    .password-requirements {
      background-color: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      font-size: 13px;
    }
    .password-requirements ul {
      margin: 4px 0 0 0;
      padding-left: 20px;
    }
    .password-requirements li {
      margin: 4px 0;
    }
  `]
})
export class NewPasswordDialogComponent {
  private dialogRef = inject(MatDialogRef<NewPasswordDialogComponent>);

  newPassword = '';
  confirmPassword = '';
  hidePassword = true;
  hideConfirmPassword = true;
  errorMessage = '';
  isSubmitting = false;

  onSubmit(): void {
    this.errorMessage = '';

    // Validate passwords match
    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }

    // Basic password validation
    if (this.newPassword.length < 8) {
      this.errorMessage = 'Password must be at least 8 characters.';
      return;
    }

    this.isSubmitting = true;
    this.dialogRef.close(this.newPassword);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
